import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import Button from "components/ui/Button";
import { Link, Location } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { getUserFullName } from "services/users/utils";
import {
  favoriteStudentsQuery,
  removeStudentFromFavorites,
  FavoriteStudent,
} from "services/favorites";
import { lastLessonPerStudentQuery } from "services/sessions/lesson";
import { routes } from "pages/routes";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import trashIcon from "images/trash.svg";
import ConfirmDialog from "components/ui/Dialog/ConfirmDialog";
import { useUser } from "contexts/UserContext";
import {
  daysSinceEngagement,
  groupStudents,
  isPrimaryInstructor,
  QUIET_AFTER_DAYS,
  STUDENT_GROUP_ORDER,
  STUDENT_GROUP_TITLE_KEYS,
  StudentGroupName,
} from "./studentGroups";
import styles from "./StudentListSidePanel.module.scss";

type Props = {
  location: Location;
};

export default function StudentListSidePanel(props: Props) {
  const { t } = useTranslation();
  const sessionUser = useUser();
  const queryClient = useQueryClient();

  const [showPastStudents, setShowPastStudents] = useState(false);

  const favoritesResult = useQuery(favoriteStudentsQuery());
  const lastLessonsResult = useQuery(lastLessonPerStudentQuery());
  const lastLessonByStudent = lastLessonsResult.data?.last_lessons;

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: favoriteStudentsQuery.baseKey,
    });
  };

  const togglePastStudents = () => {
    setShowPastStudents(shown => !shown);
  };

  if (favoritesResult.isPending) {
    return (
      <div className={styles.panel}>
        <TalaeraSpinner />
      </div>
    );
  }

  const groups = groupStudents({
    students: favoritesResult.data?.list ?? [],
    instructorId: sessionUser.id,
    lastLessonByStudent,
  });
  const pastStudents = groups.past;

  return (
    <div className={styles.panel}>
      {STUDENT_GROUP_ORDER.map(group => (
        <StudentGroup
          key={group}
          name={group}
          students={groups[group]}
          location={props.location}
          instructorId={sessionUser.id}
          lastLessonByStudent={lastLessonByStudent}
          onRemoved={refresh}
        />
      ))}

      {pastStudents.length > 0 && (
        <div className={styles.pastGroup}>
          <button
            type="button"
            className={styles.pastToggle}
            aria-expanded={showPastStudents}
            onClick={togglePastStudents}
          >
            <span className={styles.caret} aria-hidden="true">
              {showPastStudents ? "▾" : "▸"}
            </span>
            {t("Common.studentListPanel.groupPast")}
            <span className={styles.count}>{pastStudents.length}</span>
          </button>
          {showPastStudents && (
            <ul className={styles.list}>
              {pastStudents.map(student => (
                <StudentListItem
                  key={student.id}
                  student={student}
                  location={props.location}
                  isCovering={!isPrimaryInstructor(student, sessionUser.id)}
                  quietDays={null}
                  onRemoved={refresh}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

type StudentGroupProps = {
  name: StudentGroupName;
  students: FavoriteStudent[];
  location: Location;
  instructorId: string;
  lastLessonByStudent?: Record<string, string>;
  onRemoved: () => void;
};

function StudentGroup(props: StudentGroupProps) {
  const { t } = useTranslation();

  if (props.students.length === 0) {
    return null;
  }

  return (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>
        {props.name === "quiet"
          ? t(STUDENT_GROUP_TITLE_KEYS.quiet, { days: QUIET_AFTER_DAYS })
          : t(STUDENT_GROUP_TITLE_KEYS[props.name])}
        <span className={styles.count}>{props.students.length}</span>
      </h4>
      <ul className={styles.list}>
        {props.students.map(student => (
          <StudentListItem
            key={student.id}
            student={student}
            location={props.location}
            isCovering={!isPrimaryInstructor(student, props.instructorId)}
            quietDays={
              props.name === "quiet"
                ? daysSinceEngagement(student, props.lastLessonByStudent)
                : null
            }
            onRemoved={props.onRemoved}
          />
        ))}
      </ul>
    </div>
  );
}

type StudentListItemProps = {
  student: FavoriteStudent;
  location: Location;
  isCovering: boolean;
  quietDays: number | null;
  onRemoved: () => void;
};

function StudentListItem(props: StudentListItemProps) {
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const path = routes.studentDetails.path({ id: props.student.id });
  const name = getUserFullName(props.student.profile);

  const openConfirmation = () => {
    setShowConfirmation(true);
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <li
      className={classNames(
        styles.item,
        isCurrentStudentPath(path, props.location.pathname) && styles.current
      )}
    >
      <Link className={styles.link} to={path}>
        {name}
      </Link>
      {props.quietDays !== null && (
        <span
          className={styles.quiet}
          title={t("Common.studentListPanel.quietTooltip", { count: props.quietDays })}
        >
          {formatQuietDays(t, props.quietDays)}
        </span>
      )}
      {props.isCovering && (
        <span
          className={styles.covering}
          title={t("Common.studentListPanel.coveringTooltip")}
          aria-label={t("Common.studentListPanel.coveringBadge")}
        />
      )}
      {props.isCovering && (
        <Button
          variant="plain"
          className={styles.remove}
          onClick={openConfirmation}
          aria-label={t("Common.studentListPanel.removeAction", { name })}
          title={t("Common.studentListPanel.removeAction", { name })}
        >
          <img className={styles.removeIcon} src={trashIcon} alt="" />
        </Button>
      )}
      {showConfirmation && (
        <ConfirmDialog
          title={t("Common.studentListPanel.removeTitle")}
          confirmLabel={t("Common.actions.remove")}
          danger
          message={t("Common.studentListPanel.removeMessage", { name })}
          onConfirm={() => removeStudentFromFavorites(props.student.id)}
          onClose={removed => {
            closeConfirmation();
            if (removed) {
              props.onRemoved();
            }
          }}
        />
      )}
    </li>
  );
}

/**
 * The student route carries an optional tab segment, so `/student/42/lessons` has to
 * keep the `/student/42` row highlighted.
 */
function isCurrentStudentPath(studentPath: string, pathname: string) {
  return pathname === studentPath || pathname.startsWith(`${studentPath}/`);
}

function formatQuietDays(t: TFunction, days: number) {
  return days >= 365
    ? t("Common.studentListPanel.quietMonths", { months: Math.floor(days / 30) })
    : t("Common.studentListPanel.quietDays", { days });
}
